import { amber } from '@mui/material/colors';
import { bindActionCreators } from 'redux';
import { Collapse } from '@mui/material';
import { connect } from 'react-redux';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import HelpIcon from '@mui/icons-material/Help';
import InputLabel from '@mui/material/InputLabel';
import LockIcon from '@mui/icons-material/Lock';
import NativeSelect from '@mui/material/NativeSelect';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';

import { navigateInvoiceDetails } from '../actions/stepper-actions';
import { setTestId } from '../util/testUtils';
import { StateOptions } from '../constants/app-constants';
import { submit, update } from '../actions/payment-actions';
import PaymentAmountControl from './payment-amount-control';
import PaymentMethodControl from './payment-method-control';
import paymentTypes from '../constants/payment-type-constants';

const amber800 = amber['800'];

const styles = (theme) => ({
  root: {
    position: 'relative',
  },
  title: {
    margin: theme.spacing(1, 0, 1),
  },
  subtitle: {
    margin: theme.spacing(6, 0, 2),
  },
  divider: {
    marginBottom: theme.spacing(4),
  },
  rowStyles: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  textFieldStyles: {
    marginRight: 24,
  },
  slashStyles: {
    alignSelf: 'flex-end',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontSize: 24,
    fontWeight: 300,
    lineHeight: 1,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    width: 24,
  },
  remember: {
    display: 'flex',
    marginTop: theme.spacing(2),
    alignItems: 'center',
  },
  formControl: {
    margin: theme.spacing(0, 1, 0, 1),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(3, 1, 2),
    width: '98%',
  },
  secure: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  lock: {
    color: amber800,
  },
  paymentMethod: {
    marginBottom: theme.spacing(2),
  },
  label: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
  },
  radioGroup: {
    marginBottom: theme.spacing(3),
  },
  help: {
    color: theme.palette.text.secondary,
  },
  helpCell: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexGrow: 2,
  },
  error: {
    width: '100%',
  },
  inputStyle: {
    width: '100%',
  },
});

class PaymentForm extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    payment: PropTypes.shape({
      billingAddress1: PropTypes.string,
      billingAddress1Error: PropTypes.string,
      billingAddress2: PropTypes.string,
      billingAddress2Error: PropTypes.string,
      billingCity: PropTypes.string,
      billingCityError: PropTypes.string,
      billingState: PropTypes.string,
      billingStateError: PropTypes.string,
      billingZipCode: PropTypes.string,
      billingZipCodeError: PropTypes.string,
      cardError: PropTypes.string,
      cardNumber: PropTypes.string,
      cardNumberError: PropTypes.string,
      ccExpirationMonth: PropTypes.string,
      ccExpirationMonthError: PropTypes.string,
      ccExpirationYear: PropTypes.string,
      ccExpirationYearError: PropTypes.string,
      cvn: PropTypes.string,
      cvnError: PropTypes.string,
      firstName: PropTypes.string,
      firstNameError: PropTypes.string,
      isPostingCard: PropTypes.bool,
      lastName: PropTypes.string,
      lastNameError: PropTypes.string,
      paymentAmountError: PropTypes.string,
      paymentType: PropTypes.string.isRequired,
      saveCC: PropTypes.bool,
    }).isRequired,
    creditCard: PropTypes.shape({
      ccDisplay: PropTypes.string,
    }).isRequired,
    onNavigateInvoiceDetails: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
  };

  handleFormKeyDown = (event) => {
    const { onSubmit } = this.props;
    // Handle Enter key like the submit button
    if (event.key === 'Enter') {
      onSubmit();
    }
  };

  // eslint-disable-next-line react/sort-comp
  renderExistingCardForm = () => {
    // TODO: should this be it's own component?
    const { classes, creditCard, payment, onUpdate } = this.props;

    const { cvn, cvnError } = payment;

    return (
      <div>
        <Grid className={classes.paymentMethod} container>
          <Grid className={classes.rowStyles} item sm={8} xs={12}>
            <FormControl>
              <InputLabel shrink>Card Number</InputLabel>
              <FormLabel className={classes.label}>
                {creditCard.ccDisplay}
              </FormLabel>
            </FormControl>
          </Grid>
          <Grid className={classes.rowStyles} item sm={4} xs={12}>
            <TextField
              {...setTestId('cvc')}
              error={Boolean(cvnError)}
              helperText={cvnError || ' '}
              label="CVN Code *"
              onChange={(e) => onUpdate({ cvn: e.target.value })}
              onKeyDown={this.handleFormKeyDown}
              placeholder="123"
              value={cvn}
            />
          </Grid>
        </Grid>
      </div>
    );
  };

  renderNewCardForm = () => {
    const { classes, payment, onUpdate } = this.props;

    const {
      billingAddress1,
      billingAddress1Error,
      billingAddress2,
      billingAddress2Error,
      billingCity,
      billingCityError,
      billingState,
      billingStateError,
      billingZipCode,
      billingZipCodeError,
      cardNumber,
      cardNumberError,
      ccExpirationMonth,
      ccExpirationMonthError,
      ccExpirationYear,
      ccExpirationYearError,
      cvn,
      cvnError,
      firstName,
      firstNameError,
      lastName,
      lastNameError,
      saveCC,
    } = payment;

    return (
      <Fragment>
        <div className={classes.rowStyles}>
          <TextField
            {...setTestId('firstName')}
            autoFocus
            className={classes.textFieldStyles}
            error={Boolean(firstNameError)}
            fullWidth
            helperText={firstNameError}
            label="First Name *"
            name="firstName"
            onChange={(e) => onUpdate({ firstName: e.target.value })}
            placeholder="James"
            value={firstName}
          />
          <TextField
            {...setTestId('lastName')}
            error={Boolean(lastNameError)}
            fullWidth
            helperText={lastNameError}
            label="Last Name *"
            name="lastName"
            onChange={(e) => onUpdate({ lastName: e.target.value })}
            placeholder="Smith"
            value={lastName}
          />
        </div>
        <div className={classes.rowStyles}>
          <TextField
            {...setTestId('cardNumber')}
            error={Boolean(cardNumberError)}
            fullWidth
            helperText={cardNumberError}
            label="Card Number *"
            name="cardNumber"
            onChange={(e) => onUpdate({ cardNumber: e.target.value })}
            placeholder="4444333322221111"
            value={cardNumber}
          />
        </div>
        <Grid container>
          <Grid className={classes.rowStyles} item md={4} xs={5}>
            <TextField
              {...setTestId('expiryMonth')}
              error={Boolean(ccExpirationMonthError)}
              fullWidth
              helperText={ccExpirationMonthError}
              label="Exp Month *"
              name="ccExpirationMonth"
              onChange={(e) => onUpdate({ ccExpirationMonth: e.target.value })}
              placeholder="05 for May"
              value={ccExpirationMonth}
            />
          </Grid>
          <Grid className={classes.slashStyles} item xs={1}>
            /
          </Grid>
          <Grid className={classes.rowStyles} item md={4} xs={6}>
            <TextField
              {...setTestId('expiryYear')}
              className={classes.textFieldStyles}
              error={Boolean(ccExpirationYearError)}
              fullWidth
              helperText={ccExpirationYearError}
              label="Exp Year *"
              name="ccExpirationYear"
              onChange={(e) => onUpdate({ ccExpirationYear: e.target.value })}
              placeholder="22 for 2022"
              value={ccExpirationYear}
            />
          </Grid>
          <Grid className={classes.rowStyles} item md={3} xs={12}>
            <TextField
              {...setTestId('cvcNew')}
              className={classes.inputStyle}
              error={Boolean(cvnError)}
              helperText={cvnError}
              label="CVN Code *"
              onChange={(e) => onUpdate({ cvn: e.target.value })}
              placeholder="123"
              value={cvn}
            />
          </Grid>
        </Grid>
        <Typography className={classes.subtitle} variant="subtitle2">
          Billing Address
        </Typography>
        <div className={classes.rowStyles}>
          <TextField
            {...setTestId('billingAddress1')}
            error={Boolean(billingAddress1Error)}
            fullWidth
            helperText={billingAddress1Error}
            label="Billing Address 1 *"
            name="billingAddress1"
            onChange={(e) => onUpdate({ billingAddress1: e.target.value })}
            placeholder="123 Main Street"
            value={billingAddress1}
          />
        </div>
        <div className={classes.rowStyles}>
          <TextField
            {...setTestId('billingAddress2')}
            error={Boolean(billingAddress2Error)}
            fullWidth
            helperText={billingAddress2Error}
            label="Billing Address 2 (Optional)"
            name="billingAddress2"
            onChange={(e) => onUpdate({ billingAddress2: e.target.value })}
            placeholder="Apt 1234"
            value={billingAddress2}
          />
        </div>
        <Grid container>
          <Grid item md={4} xs={6}>
            <TextField
              {...setTestId('city')}
              error={Boolean(billingCityError)}
              fullWidth
              helperText={billingCityError}
              label="City *"
              name="billingCity"
              onChange={(e) => onUpdate({ billingCity: e.target.value })}
              placeholder="Dallas"
              value={billingCity}
            />
          </Grid>
          <Grid item md={5} xs={6}>
            <FormControl
              {...setTestId('billingStateControl')}
              className={classes.formControl}
              error={Boolean(billingStateError)}
            >
              <InputLabel htmlFor="billing-state" shrink>
                State *
              </InputLabel>
              <NativeSelect
                value={billingState || ''}
                {...setTestId('billingState')}
                className={classes.selectEmpty}
                onChange={(e) => onUpdate({ billingState: e.target.value })}
              >
                {StateOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.text}
                  </option>
                ))}
              </NativeSelect>
              <FormHelperText>{billingStateError}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item md={3} xs={12}>
            <TextField
              {...setTestId('zipCode')}
              error={Boolean(billingZipCodeError)}
              fullWidth
              helperText={billingZipCodeError}
              label="Zip Code *"
              name="billingZipCode"
              onChange={(e) => onUpdate({ billingZipCode: e.target.value })}
              placeholder="75095"
              value={billingZipCode}
            />
          </Grid>
        </Grid>
        <div className={classes.remember}>
          <div className={classes.rememberCell}>
            <FormControlLabel
              control={
                <Checkbox
                  {...setTestId('saveCard')}
                  color="primary"
                  name="saveCard"
                  onChange={() => onUpdate({ saveCC: !saveCC })}
                  value={saveCC}
                />
              }
              label="Save card details to account"
            />
          </div>
          <div className={classes.helpCell}>
            <Tooltip placement="top" title="Replace the existing card on file.">
              <HelpIcon className={classes.help} />
            </Tooltip>
          </div>
        </div>
      </Fragment>
    );
  };

  getPaymentForm = (paymentType) => (
    <React.Fragment>
      <Collapse in={paymentType === paymentTypes.NEW}>
        {this.renderNewCardForm()}
      </Collapse>
      <Collapse in={paymentType === paymentTypes.EXISTING}>
        {this.renderExistingCardForm()}
      </Collapse>
    </React.Fragment>
  );

  isButtonDisabled = (hasErrors, isPostingCard) => {
    if (hasErrors || isPostingCard) {
      return true;
    }

    return undefined;
  };

  render() {
    const { classes, payment, onSubmit, onNavigateInvoiceDetails } = this.props;

    const {
      paymentType,
      cardNumberError,
      ccExpirationMonthError,
      ccExpirationYearError,
      cvnError,
      firstNameError,
      lastNameError,
      paymentAmountError,
      isPostingCard,
      cardError,
    } = payment;

    const formHasError = Boolean(
      cardNumberError ||
        ccExpirationMonthError ||
        ccExpirationMonthError ||
        ccExpirationYearError ||
        cvnError ||
        firstNameError ||
        lastNameError ||
        paymentAmountError
    );

    return (
      <div className={classes.root}>
        <div className={classes.secure}>
          <Tooltip title="Secure form">
            <LockIcon className={classes.lock} />
          </Tooltip>
        </div>

        <Grid className={classes.radioGroup} item xs={12}>
          <PaymentAmountControl />
        </Grid>
        <Grid className={classes.radioGroup} item xs={12}>
          <PaymentMethodControl />
        </Grid>

        {this.getPaymentForm(paymentType)}
        <Grid container>
          <Grid item xs={6}>
            <Button
              className={classes.button}
              disabled={this.isButtonDisabled(formHasError, isPostingCard)}
              onClick={onNavigateInvoiceDetails}
              variant="outlined"
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              {...setTestId('next')}
              className={classes.button}
              color="primary"
              disabled={this.isButtonDisabled(formHasError, isPostingCard)}
              onClick={onSubmit}
              variant="contained"
            >
              {isPostingCard ? (
                <CircularProgress color="inherit" size={25} />
              ) : (
                'Next'
              )}
            </Button>
          </Grid>
        </Grid>
        {cardError && (
          <Typography align="center" className={classes.error} color="error">
            We were unable to process your credit card. Please contact the
            issuing bank for assistance.
          </Typography>
        )}
        <FormLabel className={classes.formLabel} component="legend">
          * indicates required fields
        </FormLabel>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    payment: state.payment,
    creditCard: state.invoice.creditCard,
    balanceDue: state.invoice.balanceDue,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onUpdate: update,
      onSubmit: submit,
      onNavigateInvoiceDetails: navigateInvoiceDetails,
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(PaymentForm));
